/*
 * $Id:: StepThree.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState } from 'react';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import { ReactComponent as ListArrowIcon } from '../../../../assets/images/arrow_right_24px.svg';
import { ReactComponent as CheckedIcon } from '../../../../assets/images/check_box_24px.svg';
import { ReactComponent as UncheckedIcon } from '../../../../assets/images/check_box_outline_blank_24px.svg';
import { ReactComponent as LinkArrowIcon } from '../../../../assets/images/open_in_new_24px.svg';
import Recaptcha from '../../../../components/Recaptcha';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    title: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: theme.palette.text.mainGreen,
    },
    buttonGroup: {
        marginTop: '40px',
    },
    fieldsList: {
        marginBottom: '20px',
        wordBreak: 'break-word',
    },
    listItem: {
        marginTop: '10px',
        paddingLeft: '8px',
    },
    listIcon: {
        '& path': {
            fill: theme.palette.text.listgrey,
        },
    },
    checkbox: {
        marginLeft: '-6px',
    },
    checkboxLabel: {
        color: '#086B9C',
        fontSize: '0.875rem',
        textDecoration: 'underline',
        marginLeft: '4px',
        marginRight: '10px',
    },
    editButton: {
        minWidth: '54px',
        marginLeft: '0',
        marginRight: '18px',
    },
    submitButton: {
        minWidth: '100px',
        background: theme.palette.text.mainGreen,
        borderColor: theme.palette.text.mainGreen,
    },
}));

const StepThree = ({ accountDetails = [], backToStepTwo, register, consent, shouldConsent, recaptchaSiteKey }) => {
    const classes = useStyles();

    const [hasAgreed, setHasAgreed] = useState({
        privacyPolicy: consent.privacyPolicy,
        gdprConsent: consent.gdprConsent,
    });

    const [recaptchaApproved, setRecaptchaApproved] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState('');

    const submitButton = (event) => {
        event.preventDefault();
        const { privacyPolicy, gdprConsent } = hasAgreed;
        if (shouldConsent && (!privacyPolicy || !gdprConsent)) return;
        register({ privacyPolicy, gdprConsent });
    };

    const handleChange = (event) => {
        setHasAgreed({ ...hasAgreed, [event.target.name]: event.target.checked });
    };

    const checkboxLabel = (label) => (
        <div>
            <span className={classes.checkboxLabel}>{t(label)}</span>
            <span>
                <LinkArrowIcon aria-hidden="true" focusable="false" />
            </span>
        </div>
    );

    let disableCreateUserButton = !recaptchaApproved;
    if (recaptchaApproved) {
        disableCreateUserButton = shouldConsent && (!hasAgreed.privacyPolicy || !hasAgreed.gdprConsent);
    }

    return (
        <div>
            <Typography className={classes.title}>{t('your.account.details')}</Typography>
            <List disablePadding className={classes.fieldsList}>
                {accountDetails.map((value, index) => (
                    <ListItem key={`${value}_${index}`} className={classes.listItem}>
                        <ListItemIcon>
                            <ListArrowIcon className={classes.listIcon} aria-hidden="true" focusable="false" />
                        </ListItemIcon>
                        <ListItemText primary={value}></ListItemText>
                    </ListItem>
                ))}
            </List>
            {shouldConsent && <Typography className={classes.title}>{t('data.treatment.policy.consent')}</Typography>}
            {shouldConsent && (
                <FormGroup>
                    <FormControlLabel
                        className={classes.checkbox}
                        control={
                            <Checkbox
                                role="checkbox"
                                checked={hasAgreed.privacyPolicy}
                                onChange={handleChange}
                                name="privacyPolicy"
                                checkedIcon={<CheckedIcon aria-hidden="true" focusable="false" />}
                                icon={<UncheckedIcon aria-hidden="true" focusable="false" />}
                            />
                        }
                        label={checkboxLabel('privacy.policy.link')}
                    />
                    <FormControlLabel
                        className={classes.checkbox}
                        control={
                            <Checkbox
                                role="checkbox"
                                checked={hasAgreed.gdprConsent}
                                onChange={handleChange}
                                name="gdprConsent"
                                checkedIcon={<CheckedIcon aria-hidden="true" focusable="false" />}
                                icon={<UncheckedIcon aria-hidden="true" focusable="false" />}
                            />
                        }
                        label={checkboxLabel('gdpr.consent')}
                    />
                </FormGroup>
            )}
            <Grid container spacing={0} className={classes.buttonGroup}>
                <Grid item></Grid>
                <Grid item>
                    <Button variant="outlined" type="button" className={classes.editButton} onClick={backToStepTwo}>
                        {t('edit')}
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" className={classes.submitButton} onClick={submitButton} disabled={disableCreateUserButton}>
                        {t('create.user')}
                    </Button>
                </Grid>
            </Grid>{' '}
            {!!recaptchaError && (
                <Grid item xs sm md>
                    <Typography color="error">{t('error.recaptcha')}</Typography>
                </Grid>
            )}
            {recaptchaSiteKey && (
                <Recaptcha
                    siteKey={recaptchaSiteKey}
                    onVerify={() => setRecaptchaApproved(true)}
                    recaptchaApproved={recaptchaApproved}
                    onRecaptchaError={() => setRecaptchaError(true)}
                />
            )}
        </div>
    );
};

export default StepThree;
